import React, { useEffect, useState } from "react";
import "./Enquiry.scss";
import {
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  EyeOutlined,
  DeleteOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import {
  Table,
  Button,
  Input,
  Tabs,
  Modal,
  message,
  Popconfirm,
  Dropdown,
  Switch,
  Space,
  Tag
} from "antd";
import { getApi, deleteApi, putApi } from "../../Utlis/apiService";
import { GET_ALL_ENQUIRY_LIST, DELETE_ENQUIRY_BY_ID, UPDATE_ENQUIRY_BY_ID } from "../../Utlis/apiPath";
import { debounce } from "lodash";
import Loader from "../../Components/Loader/Loader";
import { Typography, Row, Col } from "antd";
const { Text } = Typography;
const { TabPane } = Tabs;

const NEW_ENQUIRY_WINDOW_MS = 24 * 60 * 60 * 1000; // flag anything created in the last 24h as New

const isRecentEnquiry = (record) =>
  Boolean(record?.createdAt) &&
  Date.now() - new Date(record.createdAt).getTime() < NEW_ENQUIRY_WINDOW_MS;

const toTitleCase = (text) => {
  if (!text || text.includes("@")) return text || "";
  return text
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const Enquiry = () => {
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [isAscending, setIsAscending] = useState("");
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("latest");
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [pendingCheckedValue, setPendingCheckedValue] = useState(false);

  const getPageSizeOptions = () => {
    return totalRecords <= 50 ? ["10", "20", "50"] : ["15", "25", "50", "100"];
  };

  const fetchTableData = async (page = 1, pageSize = 10, searchValue = "", tab = "latest") => {
    setIsLoading(true);
    try {
      // API expects 0-based pageIndex; UI uses 1-based
      const apiPageIndex = Math.max(0, (page || 1) - 1);
      const params = { 
        pageIndex: apiPageIndex, 
        pageSize, 
        search: searchValue,
        isActive: tab === "latest" ? true : false // Filter based on active tab
      };
      const response = await getApi(GET_ALL_ENQUIRY_LIST, { params });

      if (response.status === 200 && response.data) {
        const items = response.data.items || [];
        const total = response.data.total || 0;
        const respPageIndex = response.data.pageIndex !== undefined ? response.data.pageIndex + 1 : page;
        const respPageSize = response.data.pageSize || pageSize;

        setTableData(items);
        setTotalRecords(total);
        setPageIndex(respPageIndex);
        setPageSize(respPageSize);
      } else {
        setTableData([]);
        setTotalRecords(0);
      }
    } catch (error) {
      setTableData([]);
      console.error("Error fetching table data:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setTableData([]);
    fetchTableData(1, 10, search, activeTab);
  }, [isAscending, activeTab]);

  const handleSorting = () => {
    setIsAscending((prevState) => (prevState === "" ? false : !prevState));
  };

  const handleSearch = debounce((value) => {
    setSearch(value);
    fetchTableData(1, pageSize, value, activeTab);
  }, 500);

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    setPageIndex(current);
    setPageSize(pageSize);
    fetchTableData(current, pageSize, search, activeTab);
  };

  const showEnquiryDetails = (record) => {
    setSelectedInstructor(record);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedInstructor(null);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPageIndex(1);
    setSearch("");
  };

  // Delete Enquiry
  const handleDelete = async (id) => {
    setIsLoading(true);
    try {
      const response = await deleteApi(`${DELETE_ENQUIRY_BY_ID}?id=${id}`);
      if (response.status === 200) {
        message.success("Enquiry deleted successfully");
        fetchTableData(pageIndex, pageSize, search, activeTab);
      } else {
        message.error("Failed to delete enquiry");
      }
    } catch (error) {
      console.error("Error deleting enquiry:", error);
      message.error("Error deleting enquiry");
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle isActive status
  const handleToggleStatus = async (record, checked) => {
    setIsLoading(true);
    try {
      const updatedData = {
        isActive: checked,
        message: record.message || "Followed up with the client",
        action: "markViewed"
      };
      const response = await putApi(`${UPDATE_ENQUIRY_BY_ID}?id=${record.id}`, updatedData);
      if (response.status === 200) {
        message.success(`Enquiry ${checked ? "activated" : "disposed"} successfully`);
        fetchTableData(pageIndex, pageSize, search, activeTab);
      } else {
        message.error("Failed to update enquiry status");
      }
    } catch (error) {
      console.error("Error updating enquiry status:", error);
      message.error("Error updating enquiry status");
    } finally {
      setIsLoading(false);
    }
  };
  // Handler for toggle click
const handleToggleClick = (record, checked) => {
  setSelectedRecord(record);
  setPendingCheckedValue(checked);
  setIsConfirmModalVisible(true);
};

// Confirm handler
const handleConfirmToggle = () => {
  handleToggleStatus(selectedRecord, pendingCheckedValue);
  setIsConfirmModalVisible(false);
  setSelectedRecord(null);
};

// Cancel handler
const handleCancelToggle = () => {
  setIsConfirmModalVisible(false);
  setSelectedRecord(null);
};

  // Get menu items for dropdown
  const getMenuItems = (record) => [
    {
      key: 'view',
      icon: <EyeOutlined />,
      label: 'View Details',
      onClick: () => showEnquiryDetails(record),
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: (
        <Popconfirm
          title="Are you sure you want to delete this enquiry?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
          placement="left"
        >
          <span>Delete</span>
        </Popconfirm>
      ),
      danger: true,
    },
    {
      key: 'toggle',
      label: (
        <div onClick={(e) => e.stopPropagation()}>
          <Space>
            <span>Active Status</span>
            <Switch
              checked={record.isActive}
              onChange={(checked) => handleToggleClick(record, checked)}
              size="small"
            />
          </Space>
        </div>
      ),
    },
  ];

  const instructorListColumns = [
    {
      title: "SL No",
      key: "slNo",
      render: (text, record, index) => (pageIndex - 1) * pageSize + index + 1,
    },
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      render: (fullName, record) => (
        <Space size={6}>
          <span>{toTitleCase(fullName)}</span>
          {isRecentEnquiry(record) && (
            <Tag color="green" style={{ marginRight: 0, fontWeight: 600 }}>
              New
            </Tag>
          )}
        </Space>
      ),
    },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Mobile Number", dataIndex: "mobileNumber", key: "mobileNumber" },
    { title: "Category", dataIndex: "category", key: "category" },
    {
      title: "Date",
      key: "date",
      render: (_, record) =>
        record.createdAt
          ? new Date(record.createdAt).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "—",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Dropdown
          menu={{ items: getMenuItems(record) }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            icon={<MoreOutlined />}
            style={{
              border: "2px solid #1890ff",
              borderRadius: "4px",
            }}
          />
        </Dropdown>
      ),
    },
  ];

  // Columns for Disposed Enquiry (without actions)
  const disposedEnquiryColumns = [
    {
      title: "SL No",
      key: "slNo",
      render: (text, record, index) => (pageIndex - 1) * pageSize + index + 1,
    },
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      render: (fullName) => toTitleCase(fullName),
    },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Mobile Number", dataIndex: "mobileNumber", key: "mobileNumber" },
    { title: "Category", dataIndex: "category", key: "category" },
    {
      title: "Disposed Date",
      key: "disposedDate",
      render: (_, record) => (
        <span>
          {record.updatedAt 
            ? new Date(record.updatedAt).toLocaleDateString() 
            : new Date(record.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ];

  return (
    <div className="inst">
      {isLoading && <Loader />}
      <div className="inst_header">
        <h2>Enquiry Management</h2>
      </div>
      <div className="inst_table">
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <TabPane tab="Latest Enquiry" key="latest">
          <div className="inst_content">
            <div className="inst_content_search">
              <Input
                placeholder="Search"
                prefix={<SearchOutlined />}
                onChange={(e) => handleSearch(e.target.value)}
                value={search}
                style={{ width: 240, height: 32, margin: 0 }}
              />
              <Button
                icon={
                  isAscending === true ? (
                    <SortAscendingOutlined />
                  ) : (
                    <SortDescendingOutlined />
                  )
                }
                className="sortButton"
                onClick={handleSorting}
                style={{ height: 32, display: "flex", alignItems: "center", margin: 0 }}
              />
            </div>
          </div>
          <div className="inst_table">
            <Table
              columns={instructorListColumns}
              dataSource={tableData}
              rowKey="id"
              rowClassName={(record) =>
                isRecentEnquiry(record) ? "enquiry-row-new" : ""
              }
              pagination={{
                current: pageIndex,
                pageSize,
                total: totalRecords,
                showSizeChanger: true,
                pageSizeOptions: getPageSizeOptions(),
              }}
              loading={isLoading}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
            />
          </div>
        </TabPane>

        <TabPane tab="Completed Enquiry" key="disposed">
          <div className="inst_content">
            <div className="inst_content_search">
              <Input
                placeholder="Search"
                prefix={<SearchOutlined />}
                onChange={(e) => handleSearch(e.target.value)}
                value={search}
                style={{ width: 240, height: 32, margin: 0 }}
              />
              <Button
                icon={
                  isAscending === true ? (
                    <SortAscendingOutlined />
                  ) : (
                    <SortDescendingOutlined />
                  )
                }
                style={{ height: 32, display: "flex", alignItems: "center", margin: 0 }}
                className="sortButton"
                onClick={handleSorting}
              />
            </div>
          </div>
          <div className="inst_table">
            <Table
              columns={disposedEnquiryColumns}
              dataSource={tableData}
              rowKey="id"
              pagination={{
                current: pageIndex,
                pageSize,
                total: totalRecords,
                showSizeChanger: true,
                pageSizeOptions: getPageSizeOptions(),
              }}
              loading={isLoading}
              onChange={handleTableChange}
              scroll={{ x: "max-content" }}
            />
          </div>
        </TabPane>
      </Tabs>
      </div>
    

      {/* View Details Modal */}
      <Modal
        title="Enquiry Details"
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
      >
        {selectedInstructor && (
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text strong>Full Name:</Text>
              <br />
              <Text>{selectedInstructor.fullName}</Text>
            </Col>
            <Col span={12}>
              <Text strong>Email:</Text>
              <br />
              <Text>{selectedInstructor.email}</Text>
            </Col>
            <Col span={12}>
              <Text strong>Mobile Number:</Text>
              <br />
              <Text>{selectedInstructor.mobileNumber}</Text>
            </Col>
            <Col span={12}>
              <Text strong>Category:</Text>
              <br />
              <Text>{selectedInstructor.category}</Text>
            </Col>
            <Col span={12}>
              <Text strong>Status:</Text>
              <br />
              <Text>{selectedInstructor.isActive ? "Active" : "Disposed"}</Text>
            </Col>
            <Col span={12}>
              <Text strong>Created At:</Text>
              <br />
              <Text>
                {new Date(selectedInstructor.createdAt).toLocaleDateString()}
              </Text>
            </Col>
            <Col span={24}>
              <Text strong>Message:</Text>
              <br />
              <Text>{selectedInstructor.message || "No message provided"}</Text>
            </Col>
          </Row>
        )}
      </Modal>
      {/* Confirmation Modal */}
      <Modal
        title="Confirm Status Change"
        open={isConfirmModalVisible}
        onCancel={handleCancelToggle}
        footer={[
          <Button key="cancel" onClick={handleCancelToggle}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmToggle}>
            Confirm
          </Button>,
        ]}
        width={400}
      >
        <p>
          Are you sure you want to {pendingCheckedValue ? "activate" : "dispose"} this enquiry?
        </p>
      </Modal>
    </div>
  );
};

export default Enquiry;